import { Dialog } from "@headlessui/react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/hooks/useAuth";
import { useState, useCallback } from "react";
import axios from '@/lib/axios';
import { toast } from "react-hot-toast";
import { User } from "@/types";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import debounce from "lodash/debounce";

interface ExtendedUser {
  id: string | number;
  name: string | null;
  email: string | null;
  image: string | null;
  publicKey?: string;
  privateKey?: string;
}

interface NewMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartConversation: (userId: number) => Promise<void>;
}

export default function NewMessageModal({ isOpen, onClose, onStartConversation }: NewMessageModalProps) {
  const router = useRouter();
  const { user, loading } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = async (query: string) => {
    console.log("[NewMessageModal] Search initiated with query:", query);
    console.log("[NewMessageModal] Current user:", user);
    
    setSearchQuery(query);
    if (!query) {
      console.log("[NewMessageModal] Empty query, clearing results");
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      console.log("[NewMessageModal] Making API request");
      const response = await axios.get(`${process?.env?.NEXT_PUBLIC_API_URL}/users/search?q=${encodeURIComponent(query)}`);
      console.log("[NewMessageModal] API response:", response.data);
      
      const currentUserId = parseInt(user?.id?.toString() || "0");
      console.log("[NewMessageModal] Current user ID:", currentUserId);
      
      const users = response?.data?.users || response.data || [];
      const filteredUsers = users.filter((user: User) => user.id !== currentUserId);
      console.log("[NewMessageModal] Filtered users:", filteredUsers);
      
      setSearchResults(filteredUsers);
    } catch (error: any) {
      console.error("[NewMessageModal] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error("Failed to search users");
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query: string) => handleSearch(query), 300),
    []
  );

  const createConversation = async (userId: number) => {
    try {
      await onStartConversation(userId);
      onClose();
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast.error("Failed to create conversation");
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 p-6">
          <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            New Message
          </Dialog.Title>
          <div className="relative mb-4">
            <Input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => debouncedSearch(e?.target?.value)}
              className="w-full pl-10 bg-transparent border-gray-300 dark:border-gray-700 focus:border-blue-500 dark:focus:border-blue-500"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <div className="mt-4 space-y-2">
            {isLoading ? (
              <div className="text-center py-4">Loading...</div>
            ) : searchResults.length > 0 ? (
              searchResults?.map((user) => (
                <button
                  key={user.id}
                  onClick={() => createConversation(user.id)}
                  className="w-full text-left p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {user.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        @{user.username}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : searchQuery ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                No users found
              </div>
            ) : null}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
